import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import constants from 'app/constants'
import { usePersonaContext } from 'app/services/PersonaProvider/PersonaProvider'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import tagManager from 'app/tagManager'
import { dispatchPopStateEvent } from 'lib/util/popstate'

/**
 * The `useMintAccessories` hook
 *
 * @param {object} props - the component props
 * @returns {object} the context object
 */
export default function useMintAccessories (props) {
  const { checkoutData } = props
  const { setIsCartOpen } = useRootContext()
  const {
    account,
    checkoutAccessoriesWithWallet,
    clearCart,
    loadAccountData,
    userActivityStatus
  } = useUserContext()
  const { storeData, verifySupply } = usePersonaContext()
  const navigate = useNavigate()
  const [isPaperCheckoutOpen, setIsPaperCheckoutOpen] = useState(false)
  const [isTransactionComplete, setIsTransactionComplete] = useState(false)
  const [isOutOfStock, setIsOutOfStock] = useState(false)
  const [isVerifyingSupply, setIsVerifyingSupply] = useState(false)
  const [isUpdatingLastAccessoryId, setIsUpdatingLastAccessoryId] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const isBusyStatus = useMemo(
    () =>
      isVerifyingSupply || isUpdatingLastAccessoryId || !!userActivityStatus,
    [isVerifyingSupply, userActivityStatus, isUpdatingLastAccessoryId]
  )
  const lastAccessoryCount = useRef(-1)

  /**
   * Whether the checkout requires user to accept terms and conditions
   */
  const doesRequireUtilityTerms = useMemo(() => {
    const { itemIds } = checkoutData
    const needsTerms = itemIds.some(productId => {
      const match = storeData?.elements[productId]

      return match?.utility?.some(
        utility => utility.category !== 'digital_collectible'
      )
    })

    return needsTerms
  }, [checkoutData, storeData])

  /**
   * The paper checkout config
   */
  const paperCheckoutConfig = useMemo(() => {
    const email = account?.user?.email
    const walletAddress = account?.wallet?.address
    const { itemIds, itemQuantities, total } = checkoutData

    return {
      contractId: constants.CONTRACTS.AccessoryCollection.paperContractId,
      walletAddress,
      title: 'Accessories',
      successCallbackUrl: 'https://metropolisx.io/success',
      redirectAfterPayment: false,
      email,
      quantity: 1,
      mintMethod: {
        name: 'batchPaidMint',
        args: {
          accessoryIds: itemIds,
          amounts: itemQuantities,
          to: '$WALLET'
        },
        payment: {
          currency: 'MATIC',
          value: total.toString()
        }
      }
    }
  }, [account])

  /**
   * Handle the closing of the paper modal
   */
  const onPaperCloseModal = useCallback(() => {
    setIsPaperCheckoutOpen(false)
  }, [])

  /**
   * Update the last accessory ID
   */
  const updateLastAccessoryCount = useCallback(async () => {
    const res = await loadAccountData()

    if (res.success) {
      lastAccessoryCount.current = res.data?.detachedAccessories.length
    }

    setIsUpdatingLastAccessoryId(false)
  }, [loadAccountData])

  /**
   * Check the supply
   */
  const checkSupply = useCallback(async () => {
    setIsVerifyingSupply(true)

    const res = await verifySupply([...checkoutData.itemIds])

    if (!res.success) {
      if (res.message) {
        setErrorMessage(res.message)
      }

      if (res.outOfStock.length) {
        setIsOutOfStock(true)
      }
    }

    setIsVerifyingSupply(false)

    return res
  }, [])

  /**
   * On checkout with paper
   */
  const onCheckoutWithPaper = useCallback(async () => {
    setErrorMessage(null)
    setIsUpdatingLastAccessoryId(true)

    const supplyRes = await checkSupply()

    if (!supplyRes.success) {
      return
    }

    await updateLastAccessoryCount()

    setIsPaperCheckoutOpen(true)
  }, [checkSupply, updateLastAccessoryCount])

  /**
   * On checkout with wallet
   */
  const onCheckoutWithWallet = useCallback(async () => {
    setErrorMessage(null)
    setIsUpdatingLastAccessoryId(true)

    const supplyRes = await checkSupply()

    if (!supplyRes.success) {
      return
    }

    await updateLastAccessoryCount()

    const res = await checkoutAccessoriesWithWallet(checkoutData)

    if (!res?.success) {
      setErrorMessage(res?.message)

      tagManager.trackEvent({
        event: 'marketplace.transaction-error',
        action: { type: 'wallet' }
      })

      return
    }

    setIsTransactionComplete(true)
    clearCart()
    setIsCartOpen(false)

    tagManager.trackEvent({
      event: 'marketplace.transaction-success',
      action: { type: 'wallet' }
    })

    setTimeout(() => {
      navigate(`/account/wardrobe/?checkout=wallet&last=${lastAccessoryCount.current}&attachment=detached`)

      dispatchPopStateEvent()
    }, 0)
  }, [checkoutAccessoriesWithWallet, checkSupply, updateLastAccessoryCount])

  const oncheckoutWithThirdwebPay = useCallback(async () => {
    setErrorMessage(null)
    setIsUpdatingLastAccessoryId(true)

    const supplyRes = await checkSupply()

    if (!supplyRes.success) {
      return
    }

    await updateLastAccessoryCount()

    tagManager.trackEvent({
      event: 'marketplace.transaction-error',
      action: { type: 'creditcard' }
    })

    setIsTransactionComplete(true)
    clearCart()
    setIsCartOpen(false)

    tagManager.trackEvent({
      event: 'marketplace.transaction-success',
      action: { type: 'creditcard' }
    })

    setTimeout(() => {
      navigate(`/account/wardrobe/?checkout=creditcard&last=${lastAccessoryCount.current}&attachment=detached`)

      dispatchPopStateEvent()
    }, 0)
  }, [checkSupply, updateLastAccessoryCount])

  /**
   * Handle paper success response
   */
  const onPaperSuccess = useCallback(() => {
    setIsPaperCheckoutOpen(false)
    setIsTransactionComplete(true)
    clearCart()
    setIsCartOpen(false)

    tagManager.trackEvent({
      event: 'marketplace.transaction-success',
      action: {
        type: 'inApp'
      }
    })

    setTimeout(() => {
      navigate(`/account/wardrobe/?checkout=paper&last=${lastAccessoryCount.current}&attachment=detached`)

      dispatchPopStateEvent()
    }, 0)
  }, [])

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      errorMessage,
      isBusyStatus,
      isOutOfStock,
      isPaperCheckoutOpen,
      isTransactionComplete,
      isVerifyingSupply,
      onCheckoutWithPaper,
      onCheckoutWithWallet,
      paperCheckoutConfig,
      onPaperCloseModal,
      onPaperSuccess,
      doesRequireUtilityTerms,
      setErrorMessage,
      checkSupply,
      oncheckoutWithThirdwebPay
    }),
    [
      errorMessage,
      isBusyStatus,
      isOutOfStock,
      isPaperCheckoutOpen,
      isTransactionComplete,
      isVerifyingSupply,
      onCheckoutWithPaper,
      onCheckoutWithWallet,
      paperCheckoutConfig,
      onPaperCloseModal,
      onPaperSuccess,
      doesRequireUtilityTerms,
      setErrorMessage,
      checkSupply,
      oncheckoutWithThirdwebPay
    ]
  )

  return ctx
}